import * as React from 'react'

import DrillDownFilter from '~/client/src/shared/components/DrillDownFilter'
import type { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import { encodeDrillDownFilterSelectionId } from '~/client/src/shared/components/DrillDownFilter/filterSelectionIds'
import { ListHeader } from '~/client/src/shared/components/ListHeader'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  FilterSelection,
  FILTER_FACET_TYPE,
  GroupKey,
  IScopeOption,
  ISortState,
  SortDirection,
  SortKey,
} from './applyMapViewsTransform'

interface IProps {
  searchTerm: string
  onSearchChange: (next: string) => void
  sort: ISortState
  onSortKeyChange: (key: SortKey) => void
  onSortDirectionChange: (direction: SortDirection) => void
  group: GroupKey
  onGroupChange: (key: GroupKey) => void
  filterSelection: FilterSelection
  onFilterApply: (selection: FilterSelection) => void
  onFilterReset: () => void
  scopeOptions: IScopeOption[]
}

const getSortOptions = (): IOptionItem[] => [
  { key: 'default', label: Localization.translator.default },
  { key: 'name', label: Localization.translator.name, glyph: 'name' },
  { key: 'type', label: Localization.translator.type },
  {
    key: 'location',
    label: Localization.translator.location,
    glyph: 'location',
  },
  { key: 'lastUpdated', label: Localization.translator.lastUpdated },
  { key: 'createdAt', label: Localization.translator.createdAt },
]

const getGroupOptions = (): IOptionItem[] => [
  { key: 'lbs', label: Localization.translator.byLBS },
  {
    key: 'scope',
    label: Localization.translator.byLocation,
    glyph: 'location',
  },
  { key: 'type', label: Localization.translator.byType },
]

export const MapViewsSidebarFiltersBar: React.FC<IProps> = ({
  searchTerm,
  onSearchChange,
  sort,
  onSortKeyChange,
  onSortDirectionChange,
  group,
  onGroupChange,
  filterSelection,
  onFilterApply,
  onFilterReset,
  scopeOptions,
}) => (
  <ListHeader.DisplayBar
    search={{ value: searchTerm, onChange: onSearchChange }}
    filter={
      <FilterChip
        filterSelection={filterSelection}
        scopeOptions={scopeOptions}
        onApply={onFilterApply}
        onReset={onFilterReset}
      />
    }
    grouping={{
      value: group === 'none' ? null : group,
      defaultValue: null,
      options: getGroupOptions(),
      onChange: key => onGroupChange((key ?? 'none') as GroupKey),
    }}
    // 'default' is the manual order (the only reorderable one), so it stays an
    // explicit option rather than None, and it has no direction. A new key goes
    // through onSortKeyChange, which picks that key's starting direction.
    sorting={{
      value: {
        key: sort.key,
        order:
          sort.key === 'default'
            ? undefined
            : sort.direction === 'asc'
              ? SortOrder.ASC
              : SortOrder.DESC,
      },
      defaultValue: { key: 'default' },
      options: getSortOptions(),
      allowNone: false,
      onChange: value => {
        if (value.key && value.key !== sort.key) {
          return onSortKeyChange(value.key as SortKey)
        }
        onSortDirectionChange(value.order === SortOrder.DESC ? 'desc' : 'asc')
      },
    }}
  />
)

interface IFilterChipProps {
  filterSelection: FilterSelection
  scopeOptions: IScopeOption[]
  onApply: (selection: FilterSelection) => void
  onReset: () => void
}

const FilterChip: React.FC<IFilterChipProps> = ({
  filterSelection,
  scopeOptions,
  onApply,
  onReset,
}) => {
  const badgeCount = filterSelection.length

  const categories: IDrillDownFilterCategory<IScopeOption>[] = [
    {
      id: 'type',
      label: Localization.translator.type,
      items: [
        {
          id: encodeDrillDownFilterSelectionId(
            FILTER_FACET_TYPE,
            'georeferenced',
          ),
          label: Localization.translator.georeferenced,
        },
        {
          id: encodeDrillDownFilterSelectionId(FILTER_FACET_TYPE, 'whiteboard'),
          label: Localization.translator.whiteboard,
        },
      ],
      getItemId: it => it.id,
      getItemLabel: it => it.label,
    },
    {
      id: 'scope',
      label: Localization.translator.location,
      items: scopeOptions,
      getItemId: it => it.id,
      getItemLabel: it => it.label,
    },
  ]

  const initialSelection = React.useMemo(
    () => [...filterSelection],
    [filterSelection],
  )

  return (
    <ListHeader.Filter badgeCount={badgeCount}>
      <DrillDownFilter.Platform
        title={Localization.translator.filterBy}
        categories={categories}
        initialSelection={initialSelection}
        onApply={onApply}
        onReset={onReset}
      />
    </ListHeader.Filter>
  )
}
